import {
	type AIVisibleMessageV1,
	AIProblemTypeV1,
	api,
	type GetAIDialogResponseV1,
	type GetAIProblemResponseV1,
} from '@/shared/api';

/**
 * Результат подготовки сессии интервью (вычисляется на сервере):
 * - `ready` — диалог существует/создан, данные загружены, можно сразу рендерить;
 * - `gate`  — нужно показать pre-start экран (сообщение + кнопка «Начать»),
 *             диалог создастся по действию пользователя.
 */
export type InterviewSession =
	| {
			mode: 'ready';
			dialogId: string;
			dialog: GetAIDialogResponseV1;
			messages: AIVisibleMessageV1[];
	  }
	| { mode: 'gate' };

type Headers = { cookie: string };

async function loadReady(
	dialogId: string,
	headers: Headers,
): Promise<InterviewSession | null> {
	const [dialogResult, messagesResult] = await Promise.all([
		api.getAiDialogV1(dialogId, { headers }),
		api.listAiDialogMessagesV1(dialogId, {}, { headers }),
	]);
	if (dialogResult.error || !dialogResult.data) return null;
	return {
		mode: 'ready',
		dialogId,
		dialog: dialogResult.data,
		messages: messagesResult.data?.messages ?? [],
	};
}

/**
 * Единая точка принятия решения о старте сессии.
 *
 * Правила:
 * 1. Есть активный диалог → загружаем и работаем (любой тип задачи).
 * 2. Нет диалога + тип `ai-interview` → gate (диалог создаётся по «Начать»).
 * 3. Нет диалога + остальные типы → создаём диалог автоматически.
 *
 * Возвращает `null`, если на каком-то шаге бэкенд ответил ошибкой —
 * вызывающая сторона трактует это как notFound().
 */
export async function prepareInterviewSession(
	problem: GetAIProblemResponseV1,
	headers: Headers,
	languageId?: number,
): Promise<InterviewSession | null> {
	if (problem.activeDialogId) {
		return loadReady(problem.activeDialogId, headers);
	}

	if (problem.type === AIProblemTypeV1.AiInterview) {
		return { mode: 'gate' };
	}

	const { data: created, error } = await api.createAiDialogV1(
		{ aiProblemId: problem.id, languageId },
		{ headers },
	);
	if (error || !created?.dialogId) return null;

	return loadReady(created.dialogId, headers);
}
